import { useRef, useEffect, useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
    ASSIGNMENT_REPLAY_SNAPSHOT_INTERVAL_MS,
    ASSIGNMENT_REPLAY_UPLOAD_INTERVAL_MS,
    clearReplayStorageForSubmission,
    createAssignmentSessionReplayRecorder,
    filterSessionReplaysForScope,
    getAssignmentReplaySessionId,
    loadReplayRegistryFromStorage,
    patchSessionReplayRegistryScope,
    registerReplayChunk,
    saveReplayRegistryToStorage,
} from 'utils/assignmentSessionReplay';
import { uploadAssignmentSessionReplayWithRetry } from 'utils/uploadAssignmentSessionReplay';
import {
    getReplayResumeOnceSessionStorageKey,
    rotateAssignmentClientSessionId,
} from 'utils/assignmentSessionTracking';
import { useParentOrgId } from 'store/organizations/selectors';

function getRetakeReplayInitSessionStorageKey(assignmentId, submissionId, submissionsCount) {
    return `owe_asg_replay_retake_init_${assignmentId}_${submissionId}_${submissionsCount}`;
}

function getInitialRecordingPaused(resumeContext, assignmentId) {
    if (!resumeContext || !assignmentId) return false;
    try {
        const sk = getReplayResumeOnceSessionStorageKey(assignmentId);
        return !sessionStorage.getItem(sk);
    } catch {
        return true;
    }
}

function isSubmissionCompleteValue(submissionComplete) {
    return (
        submissionComplete === true ||
        submissionComplete === 1 ||
        submissionComplete === '1' ||
        (typeof submissionComplete === 'string' &&
            String(submissionComplete).toLowerCase() === 'true')
    );
}

export function useAssignmentSessionReplay({
    enabled,
    assignmentId,
    submissionId,
    activityLogId,
    serverMetaData,
    submissionComplete,
    completedSubmissionsCount,
    resumeContext,
    sessionEnded = false,
    onRequestMetaFlush,
    onRequestImmediateFlush,
}) {
    const registryRef = useRef([]);
    const recorderRef = useRef(null);
    const uploadChainRef = useRef(Promise.resolve());
    const sessionIdRef = useRef(getAssignmentReplaySessionId());
    const submissionIdRef = useRef(submissionId);
    const assignmentIdRef = useRef(assignmentId);
    const activityLogIdRef = useRef(activityLogId);
    const lastActivityLogIdRef = useRef(activityLogId);
    const enabledRef = useRef(enabled);
    const serverMetaRef = useRef(serverMetaData);
    const onRequestMetaFlushRef = useRef(onRequestMetaFlush);
    const onRequestImmediateFlushRef = useRef(onRequestImmediateFlush);
    const parentOrgId = useParentOrgId();
    const parentOrgIdRef = useRef(parentOrgId);
    const [recorderGeneration, setRecorderGeneration] = useState(0);
    const [recordingPaused, setRecordingPaused] = useState(() =>
        getInitialRecordingPaused(resumeContext, assignmentId)
    );

    submissionIdRef.current = submissionId;
    assignmentIdRef.current = assignmentId;
    activityLogIdRef.current = activityLogId;
    enabledRef.current = enabled;
    serverMetaRef.current = serverMetaData;
    onRequestMetaFlushRef.current = onRequestMetaFlush;
    onRequestImmediateFlushRef.current = onRequestImmediateFlush;
    parentOrgIdRef.current = parentOrgId;

    const getReplayScope = useCallback(
        () => ({
            submissionId: submissionIdRef.current ?? null,
            logId: activityLogIdRef.current ?? null,
        }),
        []
    );

    const clearReplayClientState = useCallback(() => {
        const sid = submissionIdRef.current;
        if (!sid) return;
        clearReplayStorageForSubmission(sid);
        registryRef.current = [];
        if (serverMetaRef.current && typeof serverMetaRef.current === 'object') {
            serverMetaRef.current = { ...serverMetaRef.current, session_replays: [] };
        }
    }, []);

    const subCount = parseInt(completedSubmissionsCount, 10) || 0;
    const submissionCompleteTruthy = isSubmissionCompleteValue(submissionComplete);
    const isRetakeAttemptInProgress =
        Boolean(submissionId) && !submissionCompleteTruthy && subCount >= 1;
    const replayTerminated = sessionEnded || submissionCompleteTruthy;

    const persistRegistry = useCallback(() => {
        const scope = getReplayScope();
        if (!scope.submissionId) return;
        saveReplayRegistryToStorage(scope.submissionId, scope.logId, registryRef.current);
    }, [getReplayScope]);

    const scheduleMetaFlush = useCallback(() => {
        const fn = onRequestMetaFlushRef.current;
        if (typeof fn !== 'function') return;
        fn();
    }, []);

    const flushMetaNow = useCallback(() => {
        const fn = onRequestImmediateFlushRef.current;
        if (typeof fn !== 'function') return Promise.resolve();
        return Promise.resolve(fn());
    }, []);

    const getRegistryForCurrentLog = useCallback(() => {
        const scope = getReplayScope();
        return filterSessionReplaysForScope(
            registryRef.current,
            scope.submissionId,
            scope.logId
        );
    }, [getReplayScope]);

    useEffect(() => {
        const prevLogId = lastActivityLogIdRef.current;
        const nextLogId = activityLogId ?? null;
        if (prevLogId != null && String(prevLogId) !== String(nextLogId ?? '')) {
            clearReplayClientState();
        }
        lastActivityLogIdRef.current = nextLogId;

        if (!submissionId) return;

        const scope = {
            submissionId,
            logId: nextLogId,
        };
        const stored = loadReplayRegistryFromStorage(submissionId, nextLogId);
        const fromStorage = filterSessionReplaysForScope(
            stored,
            scope.submissionId,
            scope.logId
        );
        const inMemory = filterSessionReplaysForScope(
            registryRef.current,
            scope.submissionId,
            scope.logId
        );
        registryRef.current = patchSessionReplayRegistryScope(
            fromStorage.length >= inMemory.length ? fromStorage : inMemory,
            scope.submissionId,
            scope.logId
        );
        persistRegistry();
    }, [submissionId, activityLogId, persistRegistry, clearReplayClientState]);

    useEffect(() => {
        if (!submissionId || !activityLogId) return;
        const scope = {
            submissionId,
            logId: activityLogId,
        };
        const pendingStored = loadReplayRegistryFromStorage(submissionId, null);
        if (pendingStored.length) {
            registryRef.current = filterSessionReplaysForScope(
                [...registryRef.current, ...pendingStored],
                scope.submissionId,
                scope.logId
            );
            saveReplayRegistryToStorage(submissionId, null, []);
        }
        registryRef.current = patchSessionReplayRegistryScope(
            registryRef.current,
            scope.submissionId,
            scope.logId
        );
        persistRegistry();
    }, [submissionId, activityLogId, persistRegistry]);

    const uploadPendingChunk = useCallback(async () => {
        const run = async () => {
            const sid = submissionIdRef.current;
            const rec = recorderRef.current;
            if (!sid || !rec) return;
            const chunk = rec.drainChunk();
            if (!chunk?.events?.length) return;
            try {
                const { url, byte_size } = await uploadAssignmentSessionReplayWithRetry(
                    sid,
                    sessionIdRef.current,
                    chunk.chunk_index,
                    chunk,
                    parentOrgIdRef.current
                );
                const scope = getReplayScope();
                registryRef.current = registerReplayChunk(registryRef.current, sessionIdRef.current, {
                    index: chunk.chunk_index,
                    url,
                    event_count: chunk.events.length,
                    recorded_at: chunk.recorded_at,
                    byte_size,
                    started_at: chunk.started_at,
                    submission_id: scope.submissionId,
                    log_id: scope.logId,
                });
                registryRef.current = patchSessionReplayRegistryScope(
                    registryRef.current,
                    scope.submissionId,
                    scope.logId
                );
                persistRegistry();
                scheduleMetaFlush();
            } catch (error) {
                Sentry.captureException(error);
            }
        };
        uploadChainRef.current = uploadChainRef.current.then(run, run);
        return uploadChainRef.current;
    }, [scheduleMetaFlush, getReplayScope, persistRegistry]);

    const finalizeRecorder = useCallback(async () => {
        const rec = recorderRef.current;
        if (!rec) return;
        recorderRef.current = null;
        try {
            rec.stop();
        } catch {
        }
        await uploadPendingChunk();
    }, [uploadPendingChunk]);

    const requestChunkUpload = useCallback(() => {
        uploadPendingChunk();
    }, [uploadPendingChunk]);

    const recordFinishLater = useCallback(async () => {
        if (!enabledRef.current || !assignmentIdRef.current) return;
        try {
            sessionStorage.removeItem(getReplayResumeOnceSessionStorageKey(assignmentIdRef.current));
        } catch {
        }
        setRecordingPaused(true);
        await finalizeRecorder();
        await flushMetaNow();
    }, [finalizeRecorder, flushMetaNow]);

    useEffect(() => {
        if (!enabled || !assignmentId || !resumeContext || replayTerminated) return;
        try {
            const sk = getReplayResumeOnceSessionStorageKey(assignmentId);
            if (sessionStorage.getItem(sk)) return;
            sessionStorage.setItem(sk, '1');
        } catch {
            return;
        }

        let cancelled = false;
        const run = async () => {
            await finalizeRecorder();
            if (cancelled) return;
            rotateAssignmentClientSessionId();
            sessionIdRef.current = getAssignmentReplaySessionId();
            setRecordingPaused(false);
            setRecorderGeneration((n) => n + 1);
            await flushMetaNow();
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [enabled, assignmentId, resumeContext, replayTerminated, finalizeRecorder, flushMetaNow]);

    useEffect(() => {
        if (!enabled || !assignmentId || !submissionId || !isRetakeAttemptInProgress) return;
        let replayRetakeSk = '';
        try {
            replayRetakeSk = getRetakeReplayInitSessionStorageKey(assignmentId, submissionId, subCount);
            if (sessionStorage.getItem(replayRetakeSk)) return;
            sessionStorage.setItem(replayRetakeSk, '1');
        } catch {
            return;
        }

        let cancelled = false;
        const run = async () => {
            await finalizeRecorder();
            if (cancelled) return;
            clearReplayClientState();
            rotateAssignmentClientSessionId();
            sessionIdRef.current = getAssignmentReplaySessionId();
            setRecordingPaused(false);
            setRecorderGeneration((n) => n + 1);
            await flushMetaNow();
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [
        enabled,
        assignmentId,
        submissionId,
        subCount,
        isRetakeAttemptInProgress,
        finalizeRecorder,
        flushMetaNow,
        clearReplayClientState,
    ]);

    useEffect(() => {
        if (!enabled || !assignmentId || recordingPaused || replayTerminated) return undefined;
        const recorder = createAssignmentSessionReplayRecorder({
            sessionId: sessionIdRef.current,
            assignmentId,
            submissionId: submissionId || null,
            logId: activityLogIdRef.current || null,
            onChunkReady: requestChunkUpload,
        });
        recorderRef.current = recorder;
        recorder.start();
        const uploadIntervalId = window.setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            recorder.takeSnapshot();
            uploadPendingChunk();
        }, ASSIGNMENT_REPLAY_UPLOAD_INTERVAL_MS);
        const snapshotIntervalId = window.setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            recorder.takeSnapshot();
        }, ASSIGNMENT_REPLAY_SNAPSHOT_INTERVAL_MS);
        return () => {
            clearInterval(uploadIntervalId);
            clearInterval(snapshotIntervalId);
            recorder.stop();
            uploadPendingChunk();
            if (recorderRef.current === recorder) {
                recorderRef.current = null;
            }
        };
    }, [
        enabled,
        assignmentId,
        submissionId,
        recorderGeneration,
        recordingPaused,
        replayTerminated,
        uploadPendingChunk,
        requestChunkUpload,
    ]);

    useEffect(() => {
        if (!enabled || !submissionId || replayTerminated) return undefined;
        const onHide = () => {
            uploadPendingChunk();
        };
        window.addEventListener('pagehide', onHide);
        return () => window.removeEventListener('pagehide', onHide);
    }, [enabled, submissionId, replayTerminated, uploadPendingChunk]);

    const getMetaForSync = useCallback(() => {
        if (!enabledRef.current) return {};
        try {
            const scope = getReplayScope();
            registryRef.current = patchSessionReplayRegistryScope(
                getRegistryForCurrentLog(),
                scope.submissionId,
                scope.logId
            );
            persistRegistry();
            return {
                session_replays: registryRef.current,
            };
        } catch (error) {
            Sentry.captureException(error);
            return {
                session_replays: getRegistryForCurrentLog(),
            };
        }
    }, [getRegistryForCurrentLog, getReplayScope, persistRegistry]);

    const onBeforeFlush = useCallback(async () => {
        if (!enabledRef.current) return;
        await uploadPendingChunk();
        await uploadChainRef.current;
    }, [uploadPendingChunk]);

    return { getMetaForSync, onBeforeFlush, recordFinishLater };
}
